import { useState, forwardRef } from 'react';
import cn from 'classnames';
import { RelatedProduct } from 'models/RelatedProduct';
import { rm } from 'utils';
import { IconArrow } from 'components/Shared/Icon/themes/BGH-RD3/iconArrow';
import { Button } from 'components/Button';
import { Checkbox } from 'components/Shared/Form';
import Markdown from 'components/Shared/Markdown';
import { RedDrawer } from 'components/Shared/Drawer';
import Price from 'components/Shared/Price';
import styles from './addon.scss';
import { useDispatch } from 'react-redux';
import { useIntersectionObserver } from 'hooks';
import { TrackedElement } from 'hooks/useIntersectionObserver';
import { GtmViewListItem } from 'store/googleTagManager/actions';
import { getAddonListName, mapRelatedProductToGtm } from 'store/googleTagManager/events/utils';
import {
  getAccumulatedPrice,
  getCategoryDisplayName,
  getCategoryDisplayNameWithUppercase,
  getDiscount,
  getDisplayName
} from 'views/ProductPage/addons/themes/BGH/utils';
import { DrawerAddonProduct } from 'views/ProductPage/addons/themes/BGH/drawerAddonProduct';
import { AddonProps } from 'views/ProductPage/addons/themes/BGH/addons';

const isSameBaseProductId = (products: RelatedProduct[]) =>
  new Set(products.map(p => p.productId.split('-')[0])).size === 1;

export const DISCOUNT_PERCENT_THRESHOLD = 10;

export const GroupAddon = forwardRef<HTMLDivElement, AddonProps>(
  ({ addon, toggleAddon, isMobile }, ref) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const openDrawer = () => setIsDrawerOpen(true);
    const closeDrawer = () => setIsDrawerOpen(false);

    const { key, displayName, products, description, isCampaign } = addon;
    const selectedProduct = products.find(p => p.selected);

    const discount = getDiscount(products);
    const shouldShowDiscount = discount >= DISCOUNT_PERCENT_THRESHOLD;

    const reduxDispatch = useDispatch();

    const { setElementRef } = useIntersectionObserver({
      onBatchVisible: handleElementsVisible
    });

    function handleElementsVisible(elements: TrackedElement<RelatedProduct>[]) {
      const gtmItemProducts = elements.map(element => {
        const viewedProduct = element.payload;
        const listName = getAddonListName(viewedProduct.productId, viewedProduct.title);
        const indexInList = products.indexOf(viewedProduct);

        return mapRelatedProductToGtm({
          product: viewedProduct,
          indexInList: indexInList,
          listName
        });
      });

      reduxDispatch(GtmViewListItem({ gtmItemProducts }));
    }

    const handleClick =
      ({
        productId,
        selected,
        quantity
      }: Pick<RelatedProduct, 'productId' | 'selected' | 'quantity'>) =>
      () => {
        const payload = {
          groupKey: key,
          productId,
          amount: quantity.amount
        };

        toggleAddon(payload, selected);
        closeDrawer();
      };

    const shouldShowCampaign = isCampaign && shouldShowDiscount;
    const text = shouldShowCampaign
      ? rm('product_campaign')
      : shouldShowDiscount || rm('product_non_campaign');

    const accumulatedPrice = selectedProduct ? getAccumulatedPrice(selectedProduct) : 0;

    const formattedDisplayName = getDisplayName({
      displayName: selectedProduct?.title,
      quantity: selectedProduct?.quantity.amount,
      unit: selectedProduct?.quantity.unit
    });

    const allAddonsHaveTheSameParent = isSameBaseProductId(products);

    return (
      <>
        {selectedProduct ? (
          <Button
            onClick={openDrawer}
            appearance="tertiary"
            className={cn(styles.addon, styles.selected, styles.selectedGroup)}
          >
            <div className={styles.name}>
              <span className={styles.addonName}>
                {getCategoryDisplayNameWithUppercase({ name: displayName })}
                <span className={styles.productsAmount}>({products.length})</span>
              </span>
              <span className={styles.productName}>
                {getCategoryDisplayNameWithUppercase({ name: formattedDisplayName })}
              </span>
            </div>

            <div className={styles.price}>
              <Checkbox
                appearance="tertiary"
                className={styles.checkbox}
                color="charcoal"
                checked
                tabIndex={-1}
                readOnly
              />
              <span className={styles.groupPrice}>
                +
                <Price
                  price={accumulatedPrice}
                  className={styles.newPrice}
                  unit={selectedProduct.price.currencyUnit}
                />
              </span>
            </div>
          </Button>
        ) : (
          <Button onClick={openDrawer} appearance="tertiary" className={styles.addon}>
            <div className={styles.name} ref={ref}>
              <span className={styles.addonName}>{rm('product_red_addons_card_header')}</span>
              <span className={styles.productName}>
                {getCategoryDisplayNameWithUppercase({ name: displayName })}
                <span className={styles.productsAmount}>({products.length})</span>
              </span>
            </div>

            <div className={styles.price}>
              {shouldShowCampaign && (
                <span className={styles.discount}>
                  <span className={styles.capitalize}>{text}</span>
                </span>
              )}
            </div>

            <IconArrow size="small" className={styles.arrow} />
          </Button>
        )}

        <RedDrawer
          title={rm(
            'product_red_addons_drawer_header',
            getCategoryDisplayName({ name: displayName }).toLowerCase()
          )}
          closeText={rm('product_red_addons_close_drawer')}
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
        >
          {allAddonsHaveTheSameParent && (
            <Markdown className={styles.description} {...description} />
          )}
          {products.map(product => (
            <DrawerAddonProduct
              ref={node => setElementRef({ ref: node, payload: product })}
              key={`${product.productId}_${product.quantity.amount}`}
              {...product}
              onClick={handleClick(product)}
              isMobile={isMobile}
            />
          ))}
        </RedDrawer>
      </>
    );
  }
);
